// The bag window: icons, stacks, tooltips, drag and drop (mouse and touch), split and throw away.
// Nothing changes until the server agrees: each action is a request, and the server's answer updates the bag.
import { BAG_SIZE, ITEMS, itemDef, type InvSlot , FOODS } from '../../../shared/items.js';
import { SKILLS, levelForXp, xpForLevel } from '../../../shared/skills.js';
import { itemIcon } from '../render/items.js';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
export type BagOp = { op: 'move'; from: number; to: number; qty?: number } | { op: 'destroy'; slot: number; qty?: number } | { op: 'equip'; from: number } | { op: 'unequip'; slot: number; to?: number } | { op: 'use'; slot: number };

export class Bag {
  slots = new Map<number, InvSlot>();
  gold = 0;
  xp: Record<string, number> = {};
  private sel = -1;
  private cells: HTMLElement[] = [];
  onOp: (op: BagOp) => void = () => {};

  constructor() {
    const grid = $('bagGrid');
    for (let i = 0; i < BAG_SIZE; i++) {
      const c = document.createElement('div'); c.className = 'cell'; c.dataset.slot = String(i);
      grid.appendChild(c); this.cells.push(c);
    }
    $('bagClose').addEventListener('click', () => this.toggle(false));
    $('bagBtn').addEventListener('click', () => this.toggle());
    this.setupDrag();
  }

  get open(): boolean { return !$('bag').hidden; }
  toggle(force?: boolean): void { $('bag').hidden = !(force ?? $('bag').hidden); if (!this.open) this.select(-1); }

  setAll(slots: InvSlot[], gold: number): void { this.slots = new Map(slots.map(s => [s.slot, s])); this.gold = gold; this.render(); }
  apply(set: InvSlot[], clear: number[], gold?: number): void {
    for (const s of clear) this.slots.delete(s);
    for (const s of set) this.slots.set(s.slot, s);
    if (gold != null) this.gold = gold;
    if (this.sel >= 0 && !this.slots.has(this.sel)) this.select(-1);
    this.render();
  }
  setXp(xp: Record<string, number>): void { this.xp = { ...xp }; this.renderSkills(); }
  addXp(skill: string, total: number): void { this.xp[skill] = total; this.renderSkills(); }

  private render(): void {
    for (let i = 0; i < BAG_SIZE; i++) {
      const c = this.cells[i], s = this.slots.get(i);
      c.textContent = ''; c.classList.toggle('sel', i === this.sel);
      c.removeAttribute('title');
      if (!s) continue;
      const d = itemDef(s.def);
      const icon = itemIcon(s.def).cloneNode() as HTMLCanvasElement;
      icon.getContext('2d')!.drawImage(itemIcon(s.def), 0, 0);
      c.appendChild(icon);
      if (s.qty > 1) { const q = document.createElement('span'); q.className = 'q'; q.textContent = String(s.qty); c.appendChild(q); }
      if (s.bound) { const b = document.createElement('span'); b.className = 'b'; c.appendChild(b); }
      c.title = d ? `${d.name}${s.qty > 1 ? ' ×' + s.qty : ''}` : s.def;
    }
    $('gold').textContent = `${this.gold.toLocaleString()} gold`;
    this.renderInfo();
  }

  private renderSkills(): void {
    const box = $('skillsLine'); box.textContent = '';
    for (const sk of SKILLS.filter(k => k.kind !== 'job')) {
      const xp = this.xp[sk.id]; if (!xp) continue;
      const lv = levelForXp(xp), lo = xpForLevel(lv), hi = xpForLevel(lv + 1), frac = hi > lo ? (xp - lo) / (hi - lo) : 1;
      const s = document.createElement('span'); s.textContent = `${sk.name} ${lv}`;
      const bar = document.createElement('span'); bar.className = 'bar'; const fill = document.createElement('i'); fill.style.width = `${Math.round(frac * 100)}%`; bar.appendChild(fill);
      s.appendChild(bar); box.appendChild(s);
    }
  }

  private select(i: number): void { this.sel = i; this.cells.forEach((c, k) => c.classList.toggle('sel', k === i)); this.renderInfo(); }

  private renderInfo(): void {
    const box = $('bagInfo'), s = this.slots.get(this.sel);
    if (!s) { box.textContent = 'Drag to move. Shift-drag to split. Double-click gear to wear it. Drag out of the bag to throw away.'; return; }
    const d = itemDef(s.def); box.textContent = '';
    const title = document.createElement('b'); title.textContent = (d?.name ?? s.def) + (s.qty > 1 ? ` ×${s.qty}` : '');
    const desc = document.createElement('div'); desc.textContent = d ? `${d.desc} ${d.kind[0].toUpperCase() + d.kind.slice(1)} · worth ${d.value}g${s.bound ? ' · bound to you' : ''}` : '';
    const acts = document.createElement('div'); acts.className = 'acts';
    const btn = (label: string, fn: () => void) => { const b = document.createElement('button'); b.type = 'button'; b.className = 'btn'; b.textContent = label; b.addEventListener('click', fn); acts.appendChild(b); };
    if (d?.equip) btn('Wear', () => { this.onOp({ op: 'equip', from: s.slot }); this.select(-1); });
    const food = FOODS[s.def];
    if (food) { btn('Eat', () => this.onOp({ op: 'use', slot: s.slot })); desc.textContent += ` · Eat: ${food.buff} (${food.desc}) for ${food.mins} min.`; }
    if (d?.restore) btn('Drink', () => this.onOp({ op: 'use', slot: s.slot }));
    if (s.qty > 1) btn('Split', () => { const to = this.firstEmpty(); if (to >= 0) this.onOp({ op: 'move', from: s.slot, to, qty: Math.floor(s.qty / 2) }); });
    btn('Throw away', () => this.confirmDestroy(s.slot));
    box.append(title, desc, acts);
  }

  private confirmDestroy(slot: number): void {
    const s = this.slots.get(slot); if (!s) return;
    const d = itemDef(s.def), box = $('bagInfo'); box.textContent = '';
    const q = document.createElement('b'); q.textContent = `Throw away ${d?.name ?? s.def}${s.qty > 1 ? ' ×' + s.qty : ''}? It's gone for good.`;
    const acts = document.createElement('div'); acts.className = 'acts';
    const yes = document.createElement('button'); yes.type = 'button'; yes.className = 'btn primary'; yes.textContent = 'Throw away';
    yes.addEventListener('click', () => { this.onOp({ op: 'destroy', slot }); this.select(-1); });
    const no = document.createElement('button'); no.type = 'button'; no.className = 'btn'; no.textContent = 'Keep it';
    no.addEventListener('click', () => this.renderInfo());
    acts.append(yes, no); box.append(q, acts);
  }

  private firstEmpty(): number { for (let i = 0; i < BAG_SIZE; i++) if (!this.slots.has(i)) return i; return -1; }

  /** Pointer events cover mouse, pen and touch: press and drag to move, tap to select, tap another slot to move there. */
  private setupDrag(): void {
    let from = -1, startX = 0, startY = 0, dragging = false, ghost: HTMLCanvasElement | null = null, over: HTMLElement | null = null, split = false;
    const cellAt = (x: number, y: number) => (document.elementFromPoint(x, y) as HTMLElement | null)?.closest<HTMLElement>('.cell') ?? null;
    $('bagGrid').addEventListener('pointerdown', e => {
      const c = (e.target as HTMLElement).closest<HTMLElement>('.cell'); if (!c) return;
      const i = Number(c.dataset.slot);
      if (!this.slots.has(i)) { if (this.sel >= 0) { this.onOp({ op: 'move', from: this.sel, to: i }); this.select(-1); } return; }
      from = i; startX = e.clientX; startY = e.clientY; dragging = false; split = e.shiftKey;
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    });
    addEventListener('pointermove', e => {
      if (from < 0) return;
      if (!dragging && Math.hypot(e.clientX - startX, e.clientY - startY) > 6) {
        dragging = true; const s = this.slots.get(from)!;
        ghost = document.createElement('canvas'); ghost.width = 12; ghost.height = 12; ghost.className = 'ghost';
        ghost.getContext('2d')!.drawImage(itemIcon(s.def), 0, 0); document.body.appendChild(ghost);
      }
      if (dragging && ghost) {
        ghost.style.left = e.clientX - 17 + 'px'; ghost.style.top = e.clientY - 17 + 'px';
        const c = cellAt(e.clientX, e.clientY); if (over !== c) { over?.classList.remove('over'); over = c; over?.classList.add('over'); }
      }
    });
    const end = (e: PointerEvent) => {
      if (from < 0) return;
      const src = from; from = -1;
      over?.classList.remove('over'); over = null; ghost?.remove(); ghost = null;
      if (!dragging) {   // a tap: select, or move the selected item onto this one (stack or swap)
        if (this.sel >= 0 && this.sel !== src && this.slots.has(this.sel)) { this.onOp({ op: 'move', from: this.sel, to: src }); this.select(-1); return; }
        this.select(this.sel === src ? -1 : src); return;
      }
      const c = cellAt(e.clientX, e.clientY);
      if (c) { const to = Number(c.dataset.slot); if (to !== src) { const s = this.slots.get(src)!; this.onOp({ op: 'move', from: src, to, qty: split && s.qty > 1 ? Math.floor(s.qty / 2) : undefined }); } return; }
      const target = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      if (target?.closest('.eq-slot, #charwin')) { if (itemDef(this.slots.get(src)!.def)?.equip) this.onOp({ op: 'equip', from: src }); return; }   // dropped on the character window: wear it
      if (!target?.closest('#bag')) { this.select(src); this.confirmDestroy(src); }
    };
    addEventListener('pointerup', end);
    $('bagGrid').addEventListener('dblclick', e => {   // double-click gear to wear it
      const c = (e.target as HTMLElement).closest<HTMLElement>('.cell'), s = c && this.slots.get(Number(c.dataset.slot));
      if (s && itemDef(s.def)?.equip) { this.onOp({ op: 'equip', from: s.slot }); this.select(-1); }
    });
    addEventListener('pointercancel', () => { from = -1; ghost?.remove(); ghost = null; over?.classList.remove('over'); over = null; });
    void ITEMS;
  }
}
